import { createStore, storageMiddleware } from '@updater-core/lib/store';
import { waitFor } from '@updater-core/lib/async';
import { SessionInteractor } from '@updater-core/interactors/shell/session/interactor.d';
import { OptimizelyInteractor } from '@updater-core/interactors/shell/optimizely/interactor.d';
import { DollyGateway } from '@updater-apps/move-your-stuff/gateways/dolly';
import { createLogger } from '@updater-apps/move-your-stuff/logger';
import { MoveItemsInteractor } from '@updater-core/interactors/move-items/types';
import { ITEM_KINDS, ITEM_STATUSES } from '@updater-core/constants/items';
import { loggerMiddleware } from '@updater-core/lib/logger';
import { ENVIRONMENT } from '@updater-core/lib/environment';
import {
  getAddressString,
  mapAddress,
  getMovePricePoint,
} from '@updater-apps/move-your-stuff/utils';
import { DomainTracker } from '@updater-core/interactors/domain-tracker';
import { PartialDeep } from '@updater-core/types/utility/PartialDeep';
import {
  DollyLinkoutExperimentName,
  DollyLinkoutVariations,
} from '@updater-apps/move-your-stuff/constants/dollyLinkOut';
import { StorageGateway } from '@updater-core/gateways/interfaces';
import {
  MysState,
  MysInteractor,
  MysSubMoveUseCase,
  InternalDollyStatus,
  MysMoveUseCase,
  DollyLinkProps,
} from './types';
import { FEATURE_FLAG, HOLDBACK_CONTROL } from './optimizely-constants';

const { log } = createLogger('[INTERACTOR]');
const storeLogger = createLogger('[STORE]');

export type MysInteractorProps = PartialDeep<{
  session: SessionInteractor;
  optimizely: OptimizelyInteractor;
  moveItems: MoveItemsInteractor;
  gateways: {
    dolly: DollyGateway;
  };
  tracker: DomainTracker;
}> & {
  gateways: {
    storage: Pick<StorageGateway, 'getJSON' | 'setJSON'>;
  };
};

export default function createInteractor({
  session,
  optimizely,
  moveItems,
  gateways: { dolly, storage },
  tracker,
}: MysInteractorProps): MysInteractor {
  const sessionState = session.getState();
  const sessionHomeSize = session.selectors.fromHomeSize(sessionState); // presumes interactor is created when authed
  const storageKey = 'upd.mys.interactor-state';
  const storedState = storage.getJSON(storageKey);
  const initialState: MysState = {
    isDollyLinkoutActive: null,
    loading: true,
    moveUseCase: null,
    subMoveUseCase: null,
    moveId: session.selectors.currentMoveId(sessionState),
    fromAddress: mapAddress(
      session.selectors.currentMoveFromAddress(sessionState)
    ),
    toAddress: mapAddress(session.selectors.currentMoveToAddress(sessionState)),
    firstName: session.selectors.userFirstName(sessionState),
    lastName: session.selectors.userLastName(sessionState),
    moveDate: session.selectors.currentMoveDate(sessionState),
    email: session.selectors.userEmail(sessionState),
    homeSize: sessionHomeSize,
    ...storedState,
  };

  const store = createStore<MysState>(initialState, [
    loggerMiddleware(storeLogger.log),
    storageMiddleware({
      storage,
      storageKey,
      includeKeys: ['moveUseCase', 'subMoveUseCase'],
    }),
  ]);

  const smallHomeSizes: string[] = ['studio', 'one_bed', 'two_bed'];

  function isDownSizingMove() {
    const homeSizes = [
      'studio',
      'one_bed',
      'two_bed',
      'three_bed',
      'three_bed_plus',
      'four_bed_plus',
    ].reduce((acc, key, index) => {
      acc[key] = index;
      return acc;
    }, {});
    const [to, from] = [
      session.selectors.toHomeSize(sessionState),
      session.selectors.fromHomeSize(sessionState),
    ];

    return homeSizes[to] < homeSizes[from];
  }

  function getDollyHandoffLink(dollyProps: object): string {
    const baseUrl = ENVIRONMENT.endpoints.dollyUi;

    const dollyLink = Object.entries(dollyProps)
      .map(([key, value]) => [key, encodeURIComponent(value)].join('='))
      .join('&');

    return `${baseUrl}?${dollyLink}`;
  }

  function getDollyDefaultProps(
    state: MysState,
    extraProps: object
  ): DollyLinkProps {
    // Pull out from, to address to convert to a string before mapping
    const {
      fromAddress,
      toAddress,
      moveUseCase,
      subMoveUseCase,
      moveId,
      moveDate,
      firstName,
      lastName,
      email,
    } = state;
    const fromAddressString = getAddressString(fromAddress);
    const toAddressString = getAddressString(toAddress);

    // construct return url
    const returnUrl =
      typeof window !== 'undefined' ? window.location.origin : '';

    const homeSizeQueryStringMap = {
      studio: 'studio',
      one_bed: '1br',
      two_bed: '2br',
      three_bed: '3br',
    };

    // props we should always set for link out
    // TODO: refactor our use_case => apartment_move_type
    // Dolly's use_case is a different value from the current mysUseCase due to eventing expectations
    // Logic is to infer Dolly's use_case based on the selection of our subMoveUseCase
    return {
      partner_name: 'updater',
      partner_transaction_id: moveId,
      location_a: fromAddressString,
      ...(MysSubMoveUseCase.HELPERS_ONLY !== subMoveUseCase
        ? { location_b: toAddressString }
        : {}),
      move_date: moveDate,
      first_name: firstName,
      last_name: lastName,
      apartment_move_type: moveUseCase,
      ...(!isDownSizingMove() && {
        apartment_size: homeSizeQueryStringMap[state.homeSize],
      }),
      email_address: email,
      partner_return_url: returnUrl,
      ...extraProps,
    };
  }

  return {
    ...store,
    // let consumers know we are initialized once loading is false
    initialized: waitFor(store, (state) => state.loading === false),
    actions: {
      fetchDollyMove: async () => {
        try {
          const [
            response,
            enabled,
            featureVariables,
            isInHoldbackControl,
            dollyLinkOutVariant,
          ] = await Promise.all([
            dolly.fetchDollyMoveQuery(),
            optimizely.actions.isFeatureEnabled(FEATURE_FLAG),
            optimizely.actions.getFeatureVariables(FEATURE_FLAG, {
              status: 'string',
            }),
            optimizely.actions.isFeatureEnabled(HOLDBACK_CONTROL),
            optimizely.actions.getVariation(DollyLinkoutExperimentName),
          ]);
          const { status: mockedStatus } = featureVariables || {};

          log('fetchDollyMove:', response);
          log('mvp feature enabled', enabled);
          log('is in holdback', isInHoldbackControl);

          tracker.actions.trackTrack('fetch_dolly_move', {
            response,
            featureEnabled: enabled,
            featureVariables,
          });

          if (!response) {
            store.setState(
              {
                eligible: false,
                loading: false,
              },
              'fetchDollyMove => ineligible due to no response'
            );
            return;
          }

          /**
           * If the home size is over a certain size the MYS Card gets suppressed.
           */
          const serviceEligible = response.eligible && !isInHoldbackControl;
          const eligibleHomeSize = smallHomeSizes.includes(sessionHomeSize);
          const shouldSeeDollyCard =
            enabled && serviceEligible && eligibleHomeSize;

          const isDollyLinkoutActive =
            dollyLinkOutVariant === DollyLinkoutVariations.VARIANT;

          tracker.actions.trackTrack('dolly_eligibility', {
            eligible: shouldSeeDollyCard,
            dollyEligible: response.eligible,
            featureEnabled: enabled,
            holdbackControl: isInHoldbackControl,
          });

          let mockedProps = {};
          if (mockedStatus) {
            const mockedPropsMap = {
              created: {
                bookedTime: new Date(store.getState().moveDate)
                  .toISOString()
                  .slice(0, 10),
              },
            };
            mockedProps = mockedPropsMap[mockedStatus];
            log('MOCKING STATUS', { status: mockedStatus, mockedProps });
          }

          store.setState(
            {
              isDollyLinkoutActive,
              eligible: shouldSeeDollyCard,
              availableOnMoveDate: response.availableOnMoveDate,
              status: mockedStatus
                ? mockedStatus.toUpperCase()
                : response.status,
              statusDate: response.statusDate,
              bookedTime: response.bookedTime,
              loading: false,
              ...mockedProps,
            },
            'fetchDollyMove'
          );
        } catch (error) {
          log('fetchDollyMove errored:', error);
          tracker.actions.trackError('fetch_dolly_move', {
            message: error.message,
            stack: error.stack,
            ...error,
          });

          store.setState(
            {
              eligible: false,
              loading: false,
            },

            `fetchDollyMove (errored)`
          );
        }
      },
      markStepAlreadyDone: async () => {
        await moveItems.actions.setStepStatus(
          ITEM_KINDS.MOVE_YOUR_STUFF,
          ITEM_STATUSES.ALREADY_DONE,
          'already_done_link'
        );
      },
      selectMoveUseCase: async (moveUseCase) => {
        store.setState(
          {
            moveUseCase,
          },
          'selectMoveUseCase'
        );
      },
      selectSubMoveUseCase: async (subMoveUseCase) => {
        store.setState(
          {
            subMoveUseCase,
          },
          'selectSubMoveUseCase'
        );
      },
      getCombinedFsmMysVariation: async () => {
        throw new Error('Not implemented');
      },
    },
    selectors: {
      isDollyLinkoutActive: (state) => state.isDollyLinkoutActive,
      homeSize: (state) => state.homeSize,
      toAddress: (state) => state.toAddress,
      loadingDollyEligibility: (state) => state.loading,
      isEligibleForDolly: (state) => state.eligible,
      isDownSizingMove,
      dollyStatus: (state): InternalDollyStatus | null =>
        state.status
          ? (state.status.toLowerCase() as InternalDollyStatus)
          : null,
      bookedTime: (state) => state.bookedTime,
      dollyStatusDate: (state) => state.statusDate,
      moveUseCase: (state) => state.moveUseCase,
      subMoveUseCase: (state) => state.subMoveUseCase,
      moveDate: (state) => state.moveDate,
      availableOnMoveDate: (state) => state.availableOnMoveDate,
      directDollyLink: (state) => {
        const defaultDollyProps = getDollyDefaultProps(state, {
          updater_test: 'landing',
        });
        return getDollyHandoffLink(defaultDollyProps);
      },
      dollyHandOffLink: (state) => {
        const { subMoveUseCase } = state;

        const defaultDollyProps = getDollyDefaultProps(state, {
          use_case:
            MysSubMoveUseCase.HELPERS_ONLY === subMoveUseCase
              ? 'inhome'
              : 'apartment',
          ...(MysSubMoveUseCase.HELPERS_ONLY === subMoveUseCase
            ? { sub_use_case: 'load_unload' }
            : {}),
        });
        return getDollyHandoffLink(defaultDollyProps);
      },
      getMovePriceValuePerUsecase: (state) => {
        const pricePoint = getMovePricePoint(state);
        const { moveUseCase } = state;

        return moveUseCase === MysMoveUseCase.WHOLE ? pricePoint : 'price';
      },
    },
    destroy: () => {
      log('destroying interactor');
    },
  };
}
